/**
 * Order utility functions
 */
import type { Order, OrderItem, CatalogItem } from '../types/orders.js'

/**
 * Calculate total price for an item (quantity * unit price)
 * @param quantity - Item quantity
 * @param pricePerUnit - Price per unit
 * @returns Total price
 */
export const calculateItemTotal = (quantity: number, pricePerUnit: number): number => {
  return quantity * pricePerUnit
}

/**
 * Calculate total amount for an order (sum of all item totals)
 * @param items - Array of order items
 * @returns Total order amount
 */
export const calculateOrderTotal = (items: OrderItem[]): number => {
  return items.reduce((total, item) => total + item.totalPrice, 0)
}

/**
 * Format order number from project name and date
 * @param projectName - Project name
 * @param date - Creation date
 * @returns Formatted order number
 */
export const formatOrderNumber = (projectName: string, date: string): string => {
  const shortName = projectName
    .split(' ')
    .map(word => word.charAt(0).toUpperCase())
    .join('')
    .slice(0, 3)
  
  const year = new Date(date).getFullYear()
  const month = String(new Date(date).getMonth() + 1).padStart(2, '0')
  
  return `${shortName}-${year}${month}`
}

/**
 * Add an item to an order
 * @param order - Current order
 * @param catalogItem - Catalog item to add
 * @param quantity - Quantity to add
 * @returns Updated order with new item
 */
export const addOrderItem = (order: Order, catalogItem: CatalogItem, quantity: number): Order => {
  const totalPrice = calculateItemTotal(quantity, catalogItem.pricePerUnit)
  
  const newItem: OrderItem = {
    id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    catalogItem,
    quantity,
    totalPrice
  }
  
  const updatedItems = [...order.items, newItem]
  
  return {
    ...order,
    items: updatedItems,
    totalAmount: calculateOrderTotal(updatedItems)
  }
}

/**
 * Remove an item from an order
 * @param order - Current order
 * @param itemId - ID of item to remove
 * @returns Updated order without the item
 */
export const removeOrderItem = (order: Order, itemId: string): Order => {
  const updatedItems = order.items.filter(item => item.id !== itemId)
  
  return {
    ...order,
    items: updatedItems,
    totalAmount: calculateOrderTotal(updatedItems)
  }
}

/**
 * Update an order item
 * @param order - Current order
 * @param itemId - ID of item to update
 * @param updates - Partial updates to apply
 * @returns Updated order with modified item
 */
export const updateOrderItem = (order: Order, itemId: string, updates: Partial<OrderItem>): Order => {
  const updatedItems = order.items.map(item => {
    if (item.id === itemId) {
      const updatedItem = { ...item, ...updates }
      
      // Recalculate total price if quantity changed
      if (updates.quantity !== undefined) {
        updatedItem.totalPrice = calculateItemTotal(updatedItem.quantity, updatedItem.catalogItem.pricePerUnit)
      }
      
      return updatedItem
    }
    return item
  })
  
  return {
    ...order,
    items: updatedItems,
    totalAmount: calculateOrderTotal(updatedItems)
  }
}

/**
 * Get status badge color for order status
 * @param status - Order status
 * @returns Color string for badge
 */
export const getStatusColor = (status: Order['status']): string => {
  switch (status) {
    case 'draft':
      return '#6B7280' // Gray
    case 'submitted':
      return '#3B82F6' // Blue
    case 'confirmed':
      return '#10B981' // Green
    case 'billed':
      return '#F59E0B' // Amber
    case 'completed':
      return '#059669' // Emerald
    default:
      return '#6B7280'
  }
}

/**
 * Format currency amount
 * @param amount - Amount to format
 * @returns Formatted currency string
 */
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount)
} 